import { ReactElement } from 'react';
import SchemaOrg from 'components/StructuredData/SchemaOrg';
import { structuredPhoneNumber } from 'lib/util';
import { IPageLocationFields } from 'types/contentful';
import { Review, ReviewAverage } from 'lib/reviewinc';

interface PhysicianSchemaProps {
  domain: string;
  currentSlug: string;
  name: string;
  npi?: number;
  officeInfo?: IPageLocationFields | null;
  secondaryLocations: IPageLocationFields[] | [];
  specialties?: string[] | undefined;
  reviewAvg?: ReviewAverage;
  reviews?: Review[];
}

export default function PhysicianSchema({
  domain,
  currentSlug,
  name,
  npi,
  officeInfo,
  secondaryLocations,
  specialties,
  reviewAvg,
  reviews,
}: PhysicianSchemaProps): ReactElement | null {
  const physicianId = `https://${domain}/${currentSlug}#physician`;
  const locations = secondaryLocations || [];
  const worksFor = locations.map((loc: IPageLocationFields) => {
    const [state] = (loc.state || '').split('-');
    return {
      '@type': 'PostalAddress',
      name: loc.name,
      streetAddress: `${loc.address1}${loc.address2 ? ` ${loc.address2}` : ''}`,
      addressLocality: loc.city,
      addressRegion: state || loc.state,
      postalCode: loc.zipCode,
      addressCountry: 'US',
    };
  });

  const telephone = officeInfo?.phoneNumber
    ? structuredPhoneNumber(officeInfo.phoneNumber)
    : locations[0]?.phoneNumber
    ? structuredPhoneNumber(locations[0].phoneNumber)
    : undefined;

  return (
    <SchemaOrg
      json={{
        '@context': 'https://schema.org',
        '@type': 'Physician',
        '@id': physicianId,
        name,
        ...(npi
          ? {
              identifier: {
                '@type': 'PropertyValue',
                propertyID: 'NPI',
                value: String(npi),
              },
            }
          : {}),
        ...(worksFor.length ? { address: worksFor } : {}),
        ...(telephone ? { telephone } : {}),
        ...(specialties && specialties.length
          ? { medicalSpecialty: specialties.length === 1 ? specialties[0] : specialties }
          : {}),
        ...(reviewAvg || reviews
          ? {
              ...(reviewAvg
                ? {
                    aggregateRating: {
                      '@type': 'AggregateRating',
                      ...(reviewAvg.fiveStarAvg ? { ratingValue: reviewAvg.fiveStarAvg } : {}),
                      ...(reviewAvg.count ? { reviewCount: reviewAvg.count } : {}),
                    },
                  }
                : {}),
              ...(reviews
                ? {
                    reviews: reviews.map((review) => ({
                      '@type': 'Review',
                      author: review.name,
                      reviewBody: review.comment,
                      reviewRating: {
                        '@type': 'Rating',
                        ratingValue: review.fiveStar,
                      },
                    })),
                  }
                : {}),
            }
          : {}),
      }}
    />
  );
}
